import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { ApplicationStatus } from '../../onboarding/models/onboarding-models';
import { ImpersonationService, PortalImpersonationState } from '../impersonation.service';

const IMPERSONATOR_PASSWORD = 'devmode123';

interface PortalRoleProfile {
  label: string;
  icon: string;
  description: string;
  state: Omit<PortalImpersonationState, 'roleLabel'>;
}

const PORTAL_ROLE_PROFILES: PortalRoleProfile[] = [
  {
    label: 'Corporate Admin (Active)',
    icon: 'fa-user-tie',
    description: 'Admin, can trade & sign, company active',
    state: {
      status: 'Active' as ApplicationStatus,
      userStatus: 'Active',
      isCorporateAdmin: true,
      canTrade: true,
      canSign: true,
    },
  },
  {
    label: 'Trade User (Active)',
    icon: 'fa-arrow-right-arrow-left',
    description: 'Can trade, cannot sign, company active',
    state: {
      status: 'Active' as ApplicationStatus,
      userStatus: 'Active',
      isCorporateAdmin: false,
      canTrade: true,
      canSign: false,
    },
  },
  {
    label: 'Sign-Only User (Active)',
    icon: 'fa-signature',
    description: 'Cannot trade, can sign documents',
    state: {
      status: 'Active' as ApplicationStatus,
      userStatus: 'Active',
      isCorporateAdmin: false,
      canTrade: false,
      canSign: true,
    },
  },
  {
    label: 'Read-Only User (Active)',
    icon: 'fa-eye',
    description: 'No trade or sign access',
    state: {
      status: 'Active' as ApplicationStatus,
      userStatus: 'Active',
      isCorporateAdmin: false,
      canTrade: false,
      canSign: false,
    },
  },
  {
    label: 'Pending KYC Admin',
    icon: 'fa-file-circle-question',
    description: 'Admin, company in PendingKYC state',
    state: {
      status: 'PendingKYC' as ApplicationStatus,
      userStatus: 'Pending',
      isCorporateAdmin: true,
      canTrade: false,
      canSign: true,
    },
  },
  {
    label: 'New Invited User',
    icon: 'fa-envelope-open-text',
    description: 'Company invited, not yet started KYC',
    state: {
      status: 'Invited' as ApplicationStatus,
      userStatus: 'Pending',
      isCorporateAdmin: false,
      canTrade: false,
      canSign: true,
    },
  },
  {
    label: 'Pending User KYC',
    icon: 'fa-id-card-clip',
    description: 'Company active, user not yet verified',
    state: {
      status: 'Active' as ApplicationStatus,
      userStatus: 'Pending',
      isCorporateAdmin: false,
      canTrade: false,
      canSign: false,
    },
  },
];

@Component({
  selector: 'app-role-impersonator',
  templateUrl: './role-impersonator.html',
  styleUrl: './role-impersonator.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoleImpersonatorComponent {
  private readonly impersonationService = inject(ImpersonationService);

  protected readonly isLocalhost = ImpersonationService.isLocalhost;
  protected readonly isPanelOpen = signal(false);
  protected readonly isUnlocked = signal(false);
  protected readonly showPasswordPrompt = signal(false);
  protected readonly passwordError = signal(false);
  protected readonly profiles = PORTAL_ROLE_PROFILES;

  protected readonly activeRole = signal<string | null>(
    this.impersonationService.getState()?.roleLabel ?? null,
  );
  protected readonly isImpersonating = signal(this.impersonationService.isActive());

  togglePanel(): void {
    if (this.isPanelOpen()) {
      this.isPanelOpen.set(false);
      return;
    }
    if (this.isUnlocked()) {
      this.isPanelOpen.set(true);
    } else {
      this.showPasswordPrompt.set(true);
      this.passwordError.set(false);
    }
  }

  submitPassword(value: string): void {
    if (value === IMPERSONATOR_PASSWORD) {
      this.isUnlocked.set(true);
      this.showPasswordPrompt.set(false);
      this.isPanelOpen.set(true);
      this.passwordError.set(false);
    } else {
      this.passwordError.set(true);
    }
  }

  cancelPassword(): void {
    this.showPasswordPrompt.set(false);
    this.passwordError.set(false);
  }

  selectRole(profile: PortalRoleProfile): void {
    const state: PortalImpersonationState = {
      roleLabel: profile.label,
      ...profile.state,
    };
    this.impersonationService.activate(state);
  }

  clearImpersonation(): void {
    this.impersonationService.clear();
    this.isUnlocked.set(false);
  }
}
